import React, { useContext, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';

import { ServiceResponse } from '../../../app/services/shared/interfaces';
import { defaultOrderDataForm, OrderDataForm } from '../../../app/models/Order';
import { Society } from '../../../app/models/Society';
import { Client } from '../../../app/models/Client';
import { TransportationQuoteDetail } from '../../../app/models/TransportationQuoteDetail';
import { WarehouseQuoteDetail } from '../../../app/models/WareHouseQuoteDetail';

import useOrderService from '../../../app/services/hooks/useOrderService';
import useTransportationOrderService from '../../../app/services/hooks/useTransportationOrderService';
import useWarehouseOrderService from '../../../app/services/hooks/useWarehouseOrderService';
import useClientService from '../../../app/services/hooks/useClientService';
import useNavigationPage from '../../../hooks/useNavigationPage';
import useSweetAlert from '../../../hooks/useSweetAlert';

import LazyLoading from '../../../components/LazyLoading';
import ButtonCancelForm from '../../../components/buttons/ButtonCancelForm';
import ButtonSaveForm from '../../../components/buttons/ButtonSaveForm';
import OrderForm from './forms/OrderForm';
import { AppContext } from '../../../contexts/AppContext';

interface Props {
    onSaved?: () => void;
    onCancel?: () => void;
    onError?: () => void;
}

const OrderCreate = ({ onSaved, onCancel, onError }: Props) => {
    const { hideLoading } = useContext(AppContext);
    const { navigationPage } = useNavigationPage();

    const { fetchingStoreOrder, storeOrder } = useOrderService();
    const { createTransportationOrder, fetchingCreateTransportationOrder, showQuoteTransportationOrder, fetchingShowQuoteTransportationOrder } =
        useTransportationOrderService();
    const { createWarehouseOrder, showQuoteWarehouseOrder, fetchingShowQuoteWarehouseOrder } =
        useWarehouseOrderService();
    const { getClientsBySociety, fetchingGetClientBySociety } = useClientService();

    const [order, setOrder] = useState<OrderDataForm>(defaultOrderDataForm);
    const [societies, setSocieties] = useState<Society[]>([]);
    const [clients, setClients] = useState<Client[]>([]);
    const [transportationQuotes, setTransportationQuotes] = useState<TransportationQuoteDetail[]>([]);
    const [warehouseQuotes, setWarehouseQuotes] = useState<WarehouseQuoteDetail[]>([]);
    const [sectors, setSectors] = useState<any[]>([]);

    const [societyAndClientDisabled, setSocietyAndClientDisabled] = useState(false);
    const [errorFields, setErrorFields] = useState<any>();

    const orderFromQuote = useRef<OrderDataForm | undefined>();
    const clientsFromQuote = useRef<Client[]>([]);
    const societyIdSelected = useRef<number | undefined>(0);

    // ── Carga inicial: datos del formulario de transporte (comunes) ───────
    useEffect(() => {
        createTransportationOrder({
            onSuccess: (response: ServiceResponse) => {
                setSocieties(response.data.societies);
                setTransportationQuotes(response.data.quotes);
                if (response.data.sectors) setSectors(response.data.sectors);
            },
            onError: (response: ServiceResponse) => {
                if (hideLoading) hideLoading();
                toast.error(response.message);
                if (onError) onError();
            },
        });

        createWarehouseOrder({
            onSuccess: (response: ServiceResponse) => {
                setWarehouseQuotes(response.data.quotes);
            },
        });
    }, []);

    // ── Al cambiar cotización de transporte ───────────────────────────────
    useEffect(() => {
        if (!order.transportation_quote_detail_id) {
            if (!order.warehouse_quote_detail_id) setSocietyAndClientDisabled(false);
            return;
        }
        showQuoteTransportationOrder(Number(order.transportation_quote_detail_id), {
            onSuccess: (response: ServiceResponse) => {
                setErrorFields(undefined);
                if (response.data.quote.society_id) {
                    societyIdSelected.current = Number(response.data.quote.society_id);
                }
                setClients(response.data.clients);
                clientsFromQuote.current = response.data.clients;
                const merged = { ...order, ...response.data.quote };
                setOrder(merged);
                orderFromQuote.current = merged;
                setSocietyAndClientDisabled(true);
            },
            onError: () => {
                setOrder({
                    ...order,
                    transportation_quote_detail_id:
                        orderFromQuote.current?.transportation_quote_detail_id ?? undefined,
                });
            },
        });
    }, [order.transportation_quote_detail_id]);

    // ── Al cambiar cotización de almacenamiento ───────────────────────────
    useEffect(() => {
        if (!order.warehouse_quote_detail_id) {
            if (!order.transportation_quote_detail_id) setSocietyAndClientDisabled(false);
            return;
        }
        showQuoteWarehouseOrder(Number(order.warehouse_quote_detail_id), {
            onSuccess: (response: ServiceResponse) => {
                setErrorFields(undefined);
                if (response.data.quote.society_id) {
                    societyIdSelected.current = Number(response.data.quote.society_id);
                }
                setClients(response.data.clients);
                clientsFromQuote.current = response.data.clients;
                const merged = { ...order, ...response.data.quote };
                setOrder(merged);
                orderFromQuote.current = merged;
                setSocietyAndClientDisabled(true);
            },
            onError: () => {
                setOrder({
                    ...order,
                    warehouse_quote_detail_id:
                        orderFromQuote.current?.warehouse_quote_detail_id ?? undefined,
                });
            },
        });
    }, [order.warehouse_quote_detail_id]);

    // ── Al cambiar sociedad → recargar clientes ───────────────────────────
    useEffect(() => {
        if (order.society_id == undefined) {
            societyIdSelected.current = undefined;
            return;
        }
        if (order.society_id && order.society_id != societyIdSelected.current) {
            const quoteHasSameSociety =
                orderFromQuote.current &&
                (orderFromQuote.current.transportation_quote_detail_id ||
                    orderFromQuote.current.warehouse_quote_detail_id) &&
                orderFromQuote.current.society_id == order.society_id;

            if (quoteHasSameSociety) {
                societyIdSelected.current = Number(order.society_id);
                setClients(clientsFromQuote.current);
                setOrder({ ...order, client_id: orderFromQuote.current!.client_id });
            } else {
                getClientsBySociety(Number(order.society_id), {
                    onSuccess: (response: ServiceResponse) => {
                        societyIdSelected.current = Number(order.society_id);
                        setClients(response.data.clients);
                        setOrder({ ...order, client_id: 0 });
                    },
                    onError: () => {
                        setOrder({ ...order, society_id: Number(societyIdSelected.current) });
                    },
                });
            }
        }
    }, [order.society_id]);

    const store = () => {
        useSweetAlert().spinnerAlert('Guardando pedido...');
        storeOrder(order, {
            onSuccess: (response: ServiceResponse) => {
                useSweetAlert().closeSpinnerAlert();
                toast.success(response.message);
                const orderId = response.data.order_id;
                const sector = order.sector === 'ALMACENAMIENTO' ? 'warehouse' : 'transport';
                navigationPage(`/pedidos/${orderId}/edit/${sector}`);
                if (onSaved) onSaved();
            },
            onError: (response: ServiceResponse) => {
                useSweetAlert().closeSpinnerAlert();
                toast.error(response.message);
                if (onError) onError();
            },
            onFieldError: (errorFields: ServiceResponse) => {
                useSweetAlert().closeSpinnerAlert();
                setErrorFields(errorFields.data);
            },
        });
    };

    const isLoading =
        fetchingCreateTransportationOrder ||
        fetchingShowQuoteTransportationOrder ||
        fetchingShowQuoteWarehouseOrder;

    return isLoading ? (
        <LazyLoading height={300} />
    ) : (
        <>
            <div className="row">
                <div className="col-12">
                    <OrderForm
                        orderForm={order}
                        setOrderForm={setOrder}
                        societies={societies}
                        clients={clients}
                        transportationQuotes={transportationQuotes}
                        warehouseQuotes={warehouseQuotes}
                        sectors={sectors}
                        errorFields={errorFields}
                        isCreated
                        loadingClient={fetchingGetClientBySociety}
                        societyAndClientDisabled={societyAndClientDisabled}
                    />
                </div>
            </div>
            <hr />
            <div className="row justify-content-end">
                <div className="col-auto">
                    <ButtonCancelForm
                        callbackCancel={() => onCancel && onCancel()}
                        locked={fetchingStoreOrder}
                    />
                </div>
                <div className="col-auto">
                    <ButtonSaveForm callbackSave={store} locked={fetchingStoreOrder} />
                </div>
            </div>
        </>
    );
};

export default OrderCreate;
